import * as mqtt from "precompiled-mqtt";
import { IConnectionSetting, IPLCEvent } from "./EventReportInterfaces";
import { MQTTEventsEnum } from "constants/constants";
import { uniqueClientId } from "mqtt/uniqueClientId";

const { connect } = mqtt;

const connectionsSettings = {
    protocol: "ws" as "ws",
    clientId: "osorno_poc_client_test",
    username: process.env.REACT_APP_MOSQUITTO_BROKER_USER,
    password: process.env.REACT_APP_MOSQUITTO_BROKER_PASSWORD,
};

const defaultMQTTBrokerSettings: IConnectionSetting = {
    allow_anonymous: false,
    host: process.env.REACT_APP_MOSQUITTO_BROKER_URL || "mqtt://test.mosquitto.org:8081",
    user: process.env.REACT_APP_MOSQUITTO_BROKER_USER,
    password: process.env.REACT_APP_MOSQUITTO_BROKER_PASSWORD,
};

const plc_topic = process.env.REACT_APP_HMI_PLC_TOPIC || "topic/plc";

/** Delay before retrying a SUBSCRIBE the broker rejected while the connection stayed up. */
export const SUBSCRIBE_RETRY_MS = 10000;

export const EventListeningEntity = {
    /**
     * Opens the persistent PLC-event listener and returns the client SYNCHRONOUSLY, so the
     * caller owns the single instance immediately and its React cleanup can always tear it down
     * (see useMQTTListener). The previous promise-based API resolved the client only after a
     * successful subscribe, so a client that was still connecting when the effect re-ran — or one
     * that never connected — escaped cleanup and leaked.
     *
     * mqtt.js owns reconnection here (reconnectPeriod): the SAME client keeps retrying while the
     * broker is down and RE-SUBSCRIBES on every (re)connect. Two rules keep that from leaking
     * sockets:
     *  - the caller must NOT create a second client to "reconnect" (mqtt.js already does), and
     *  - the error handler must NOT end the client — ending it kills the reconnect, and combined
     *    with effect re-runs that spawned a fresh reconnecting socket per drop until the browser
     *    ran out of sockets (net::ERR_INSUFFICIENT_RESOURCES). Teardown is owned solely by the
     *    caller's cleanup (end(true)).
     *
     * `onListening` fires once per successful subscribe. It is deliberately a one-way "the
     * subscription is live" signal, not a boolean toggle: the HMI's PLC-connected state must NOT
     * flap on the transient close/reconnect cycles this client goes through (a true PLC-down is
     * detected separately, via machinePlcDisconnected in the event stream).
     */
    startListening: (
        onMessage: (message: IPLCEvent) => void,
        onError: (err: Error) => void,
        onListening: () => void,
        plcAddress: string[],
        options?: IConnectionSetting,
        clientId?: string,
    ): mqtt.MqttClient => {
        const settings = options ?? defaultMQTTBrokerSettings;
        const mosquittoUrl = settings.host;
        const isWSS = mosquittoUrl.startsWith("wss");
        const topics = plcAddress.map((address) => `${plc_topic}${address ? `/${address}` : ""}`);

        const client = connect(mosquittoUrl, {
            ...connectionsSettings,
            username: settings.user,
            password: settings.password,
            protocol: isWSS ? "wss" : "ws",
            clientId: uniqueClientId(clientId || "plc-listener"),
            clean: true,
            reconnectPeriod: 10000,
        });

        // Subscribe on EVERY (re)connect so delivery resumes after a reconnect (the old one-shot
        // guard left a reconnected client silently unsubscribed). If the broker rejects the
        // SUBSCRIBE while the connection stays up (a transient or ACL SUBACK failure),
        // reconnectPeriod never re-fires — so retry the subscribe on the SAME client after a
        // delay (no new socket). The retry is guarded by client.connected, so a client that has
        // been ended (teardown) or is mid-reconnect just no-ops instead of subscribing on a dead
        // socket; and a fresh CONNECT clears any pending retry so we never double-subscribe.
        let resubscribeTimer: ReturnType<typeof setTimeout> | undefined;
        const subscribe = () => {
            client.subscribe(topics, { qos: 0 }, (err) => {
                if (err) {
                    onError(err);
                    resubscribeTimer = setTimeout(() => {
                        if (client.connected) {
                            subscribe();
                        }
                    }, SUBSCRIBE_RETRY_MS);
                    return;
                }
                onListening();
            });
        };

        client.on(MQTTEventsEnum.CONNECT, () => {
            if (resubscribeTimer) {
                clearTimeout(resubscribeTimer);
                resubscribeTimer = undefined;
            }
            subscribe();
        });

        client.on(MQTTEventsEnum.MESSAGE, (_topic, message) => {
            onMessage(JSON.parse(message.toString()));
        });

        // Surface the error but do NOT end the client — mqtt.js keeps this single client
        // reconnecting. Ending here is what turned a broker outage into a fresh socket per drop.
        // We intentionally do NOT flip a "listening" flag off here or on close: this client
        // reconnects transiently and the HMI's PLC-connected state must not flap on those cycles.
        client.on(MQTTEventsEnum.ERROR, (err) => {
            onError(err);
        });

        return client;
    },
};
